/**
 * Settings.jsx
 */

import { useState, useEffect } from "react";
import { apiFetch } from "../../components/helpers";

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
      <div
        className="card-header border-0 px-4 py-3"
        style={{
          background: "#FAFBFC",
          borderRadius: "16px 16px 0 0",
        }}
      >
        <h6 className="fw-bold mb-1" style={{ color: "#0A1628", fontSize: 14 }}>
          {title}
        </h6>

        {subtitle && (
          <p className="text-muted mb-0" style={{ fontSize: 12 }}>
            {subtitle}
          </p>
        )}
      </div>

      <div className="card-body px-4 py-4">{children}</div>
    </div>
  );
}

function FeedbackMsg({ text, ok }) {
  if (!text) return null;

  return (
    <div
      className={`alert py-2 px-3 mb-3 ${
        ok ? "alert-success" : "alert-danger"
      }`}
      style={{ fontSize: 12 }}
    >
      {text}
    </div>
  );
}

function SaveBtn({ onClick, saving, label = "Save Changes" }) {
  return (
    <button
      className="btn fw-bold text-white"
      style={{ background: "#1D9E75" }}
      onClick={onClick}
      disabled={saving}
    >
      {saving ? (
        <>
          <span className="spinner-border spinner-border-sm me-2"></span>
          Saving...
        </>
      ) : (
        label
      )}
    </button>
  );
}

function ToggleRow({ label, desc, checked, onChange }) {
  return (
    <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0A1628",
          }}
        >
          {label}
        </div>

        {desc && (
          <div className="text-muted" style={{ fontSize: 11 }}>
            {desc}
          </div>
        )}
      </div>

      <div className="form-check form-switch mb-0 ms-3">
        <input
          className="form-check-input"
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ width: 42, height: 22, cursor: "pointer" }}
        />
      </div>
    </div>
  );
}

export default function Settings() {
  /* -------------------------------- */
  /* STATES */
  /* -------------------------------- */

  const [loading, setLoading] = useState(true);

  const [clinic, setClinic] = useState({
    name: "",
    email: "",
    phone: "",
    website: "",
    gst_number: "",
    registration_number: "",
    address: "",
  });

  const [hours, setHours] = useState({
    mon_fri_open: "09:00",
    mon_fri_close: "20:00",
    sat_open: "09:00",
    sat_close: "16:00",
    sunday_closed: true,
  });

  const [clinicMsg, setClinicMsg] = useState({
    text: "",
    ok: true,
  });

  const [clinicSaving, setClinicSaving] = useState(false);

  /* -------------------------------- */
  /* LOAD DATA */
  /* -------------------------------- */

  const loadClinic = async () => {
    setLoading(true);

    try {
      const json = await apiFetch(`${process.env.REACT_APP_GET_CLINIC}`);

      if (json.status && json.data) {
        const d = json.data;

        setClinic({
          name: d.clinic_name || "",
          email: d.email || "",
          phone: d.phone || "",
          website: d.website || "",
          gst_number: d.gst_number || "",
          registration_number: d.registration_no || "",
          address: d.full_address || "",
        });

        setHours({
          mon_fri_open: d.mon_fri_open
            ? d.mon_fri_open.substring(0, 5)
            : "09:00",

          mon_fri_close: d.mon_fri_close
            ? d.mon_fri_close.substring(0, 5)
            : "20:00",

          sat_open: d.saturday_open ? d.saturday_open.substring(0, 5) : "09:00",

          sat_close: d.saturday_close
            ? d.saturday_close.substring(0, 5)
            : "16:00",

          sunday_closed: d.sunday_closed == 1,
        });
      }
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadClinic();
  }, []);

  /* -------------------------------- */
  /* SAVE DATA */
  /* -------------------------------- */

  const saveClinic = async () => {
    setClinicSaving(true);
    console.log("clinic =>", clinic);
    console.log("hours =>", hours);
    try {
      const formData = new FormData();

      formData.append("clinic_name", clinic.name);
      formData.append("email", clinic.email);
      formData.append("phone", clinic.phone);
      formData.append("website", clinic.website);
      formData.append("gst_number", clinic.gst_number);
      formData.append("registration_no", clinic.registration_number);
      formData.append("full_address", clinic.address);

      formData.append("mon_fri_open", hours.mon_fri_open);
      formData.append("mon_fri_close", hours.mon_fri_close);
      formData.append("saturday_open", hours.sat_open);
      formData.append("saturday_close", hours.sat_close);
      formData.append("sunday_closed", hours.sunday_closed ? 1 : 0);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_SAVE_CLINIC}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            clinic_name: clinic.name,
            email: clinic.email,
            phone: clinic.phone,
            website: clinic.website,
            gst_number: clinic.gst_number,
            registration_no: clinic.registration_number,
            full_address: clinic.address,

            mon_fri_open: hours.mon_fri_open,
            mon_fri_close: hours.mon_fri_close,
            saturday_open: hours.sat_open,
            saturday_close: hours.sat_close,
            sunday_closed: hours.sunday_closed ? 1 : 0,
          }),
        },
      );
      const json = await res.json();

      setClinicMsg({
        text: json.message || "Saved Successfully",
        ok: json.status,
      });
    } catch (err) {
      setClinicMsg({
        text: "Network Error",
        ok: false,
      });
    }

    setClinicSaving(false);
  };

  /* -------------------------------- */
  /* UI */
  /* -------------------------------- */

  if (loading) {
    return (
      <div className="p-4">
        <div className="spinner-border text-success"></div>
      </div>
    );
  }

  return (
    <div
      className="p-3 p-lg-4"
      style={{
        minHeight: "100vh",
        fontFamily: "'Nunito','Segoe UI',sans-serif",
      }}
    >
      <div className="mb-4">
        <h4 className="fw-bold mb-1" style={{ color: "#0A1628" }}>
          Settings
        </h4>

        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
          Manage clinic configuration and preferences
        </p>
      </div>

      {/* CLINIC INFO */}

      <SectionCard
        title="Clinic Information"
        subtitle="Details shown on invoices and reports"
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              CLINIC NAME
            </label>

            <input
              className="form-control"
              value={clinic.name}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  name: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">EMAIL</label>

            <input
              className="form-control"
              value={clinic.email}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  email: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">PHONE</label>

            <input
              className="form-control"
              value={clinic.phone}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  phone: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              WEBSITE
            </label>

            <input
              className="form-control"
              value={clinic.website}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  website: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              GST NUMBER
            </label>

            <input
              className="form-control"
              placeholder="Optional"
              value={clinic.gst_number}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  gst_number: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              REGISTRATION NO.
            </label>

            <input
              className="form-control"
              placeholder="Optional"
              value={clinic.registration_number}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  registration_number: e.target.value,
                })
              }
            />
          </div>

          <div className="col-12">
            <label className="form-label fw-bold text-muted small">
              FULL ADDRESS
            </label>

            <textarea
              rows="2"
              className="form-control"
              value={clinic.address}
              onChange={(e) =>
                setClinic({
                  ...clinic,
                  address: e.target.value,
                })
              }
            />
          </div>
        </div>

        <div className="mt-3">
          <FeedbackMsg text={clinicMsg.text} ok={clinicMsg.ok} />

          <SaveBtn onClick={saveClinic} saving={clinicSaving} />
        </div>
      </SectionCard>

      {/* WORKING HOURS */}

      <SectionCard title="Working Hours" subtitle="Set clinic operating hours">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              MON - FRI OPEN
            </label>

            <input
              type="time"
              className="form-control"
              value={hours.mon_fri_open}
              onChange={(e) =>
                setHours({
                  ...hours,
                  mon_fri_open: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              MON - FRI CLOSE
            </label>

            <input
              type="time"
              className="form-control"
              value={hours.mon_fri_close}
              onChange={(e) =>
                setHours({
                  ...hours,
                  mon_fri_close: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              SATURDAY OPEN
            </label>

            <input
              type="time"
              className="form-control"
              value={hours.sat_open}
              onChange={(e) =>
                setHours({
                  ...hours,
                  sat_open: e.target.value,
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold text-muted small">
              SATURDAY CLOSE
            </label>

            <input
              type="time"
              className="form-control"
              value={hours.sat_close}
              onChange={(e) =>
                setHours({
                  ...hours,
                  sat_close: e.target.value,
                })
              }
            />
          </div>

          <div className="col-12">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={hours.sunday_closed}
                onChange={(e) =>
                  setHours({
                    ...hours,
                    sunday_closed: e.target.checked,
                  })
                }
              />

              <label className="form-check-label">
                Clinic is closed on Sundays
              </label>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <SaveBtn onClick={saveClinic} saving={clinicSaving} />
        </div>
      </SectionCard>

      {/* NOTIFICATIONS */}

      <SectionCard
        title="Notification Preferences"
        subtitle="Choose which alerts you receive"
      >
        <ToggleRow
          label="Appointment reminders"
          desc="Remind admin of upcoming appointments"
          checked={true}
          onChange={() => {}}
        />

        <ToggleRow
          label="New patient registration"
          desc="Alert when patient is added"
          checked={true}
          onChange={() => {}}
        />

        <ToggleRow
          label="Billing reminders"
          desc="Pending payment reminders"
          checked={false}
          onChange={() => {}}
        />
      </SectionCard>
    </div>
  );
}
